import { Parser, type SyntaxNode } from 'web-tree-sitter';
import {
  assertBackgroundIndexingAllowed,
  yieldForBridgeInteractivePriority,
} from '../../files/browser/in-flight.js';
import { isSymbolIndexDeferredError } from '../symbol-index/deferred-indexing.js';
import { getLanguageKeyForFilePath } from '../language-registry.js';
import { loadLanguage, withTreeSitterRuntimeLock, type LoadedLanguage } from './tree-sitter-runtime.js';

export type ParsedSourceRoot = {
  root: SyntaxNode;
  loaded: LoadedLanguage;
  cleanup: () => void;
};

export async function parseSourceRoot(filePath: string, source: string): Promise<ParsedSourceRoot | null> {
  const languageKey = getLanguageKeyForFilePath(filePath);
  if (!languageKey) return null;

  return withTreeSitterRuntimeLock(async () => {
    await yieldForBridgeInteractivePriority();
    let loaded: LoadedLanguage | null;
    try {
      loaded = await loadLanguage(languageKey);
    } catch {
      return null;
    }
    if (!loaded) return null;

    const parser = new Parser();
    parser.setLanguage(loaded.language);
    let tree: ReturnType<Parser['parse']> = null;
    try {
      await yieldForBridgeInteractivePriority();
      assertBackgroundIndexingAllowed();
      tree = parser.parse(source);
      assertBackgroundIndexingAllowed();
      await yieldForBridgeInteractivePriority();
      if (!tree) return null;
      return {
        root: tree.rootNode,
        loaded,
        cleanup: () => {
          tree?.delete();
          parser.delete();
        },
      };
    } catch (err) {
      tree?.delete();
      parser.delete();
      if (isSymbolIndexDeferredError(err)) throw err;
      return null;
    }
  });
}
